
import RcDomainObject from "../../../common/RcDomainObject";
import Company from "../../company/Company.js";
import Employee from "../../employee/Employee.js";
import ChecklistItemResultMap from "../item/result/ChecklistItemResultMap.js";

import CheckList from "@/domain/model/checklist/CheckList.js";
import Vehicle from "@/domain/model/vehicle/Vehicle.js";
import CheckListVersion from "@/domain/model/checklist/version/CheckListVersion.js";
import ConstUtils from "@/utils/ConstUtils.js";

export default class ChecklistResult extends RcDomainObject {
  
  static MODEL_NAME = 'CheckListResult';
  
  static Fields = {
      CHECKLIST: CheckList.MODEL_NAME,
      COMPANY: Company.MODEL_NAME,
      VEHICLE: Vehicle.MODEL_NAME,
      EMPLOYEE: Employee.MODEL_NAME,
      DEFECT_TYPE: "defectType",
      RESULT_TYPE: "resultType",
      CHECKLIST_ITEM_RESULTS: ChecklistItemResultMap.MODEL_NAME,
      CHECKLIST_VERSION: CheckListVersion.MODEL_NAME,
  }
  
  constructor(domain, data) {
    super(domain, data, ChecklistResult.MODEL_NAME);
  }

  fields() {
    return ChecklistResult.Fields;
  }
  
  find() {
    var result = this.domain.checklists().results().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    return result;
  }

  checklist() {
    var data = this.get(ChecklistResult.Fields.CHECKLIST);
    return new CheckList(this.domain, data);
  }

  withChecklist(checklist) {
    this.put(ChecklistResult.Fields.CHECKLIST, checklist.data);
    return this;
  }

  withVehicle(vehicle) {
    this.put(ChecklistResult.Fields.VEHICLE, vehicle.data);
    return this;
  }

  withCompany(company) {
    this.put(ChecklistResult.Fields.COMPANY, company.data);
    return this;
  }

  withEmployee(employee) {
    this.put(ChecklistResult.Fields.EMPLOYEE, employee.data);
    return this;
  }
  
  withItemResults(itemResults) {
    this.put(ChecklistResult.Fields.CHECKLIST_ITEM_RESULTS, itemResults.data);
    return this;
  }

  vehicle() {
    var data = this.get(ChecklistResult.Fields.VEHICLE);
    return new Vehicle(this.domain, data);
  }

  company() {
    var data = this.get(ChecklistResult.Fields.COMPANY);
    return new Company(this.domain, data);
  }

  employee() {
    var data = this.get(ChecklistResult.Fields.EMPLOYEE);
    return new Employee(this.domain, data);
  }
  
  pointer() {
    var data = {'@rid': this.id()};
    var result = new ChecklistResult(this.domain, data);
    return result;
  }
  
  copy() {
    var data = this._copy();
    return new ChecklistResult(this.domain, data);
  }

  resultType() {
    return this.getString(ChecklistResult.Fields.RESULT_TYPE);
  }

  defectType() {
    return this.getString(ChecklistResult.Fields.DEFECT_TYPE);
  }

  withDefectType(defectType) {
    this.put(ChecklistResult.Fields.DEFECT_TYPE, defectType);
    return this;
  }

  // The id of the frozen checklist copy these answers were given against, or "" when the
  // result was not pinned to one (saved before copies existed, or by a server that does
  // not write them). The reference arrives as a pointer object or as the bare id.
  versionId() {
    if (!this.has(ChecklistResult.Fields.CHECKLIST_VERSION)) {
      return "";
    }
    const raw = this.get(ChecklistResult.Fields.CHECKLIST_VERSION);
    if (typeof raw === 'string') {
      return raw;
    }
    if (raw && typeof raw === 'object' && typeof raw[ConstUtils.FIELDS.ID] === 'string') {
      return raw[ConstUtils.FIELDS.ID];
    }
    return "";
  }

  hasVersion() {
    return this.versionId().length > 0;
  }

  // The frozen checklist copy, or null when the result is not pinned or the copy was not
  // sent. Looked up among the copies the inspection details reply carried; a reference
  // that arrived with the copy inlined is read as it is.
  version() {
    const versionId = this.versionId();
    if (versionId.length === 0) {
      return null;
    }
    const stored = this.domain.checklists().versions().findById(versionId);
    if (stored.id() === versionId && stored.hasSnapshot()) {
      return stored;
    }
    const raw = this.get(ChecklistResult.Fields.CHECKLIST_VERSION);
    if (raw && typeof raw === 'object') {
      const inline = new CheckListVersion(this.domain, { ...raw });
      if (inline.hasSnapshot()) {
        return inline;
      }
    }
    return null;
  }

  // The checklist as it stood when these answers were given, or null - in which case the
  // live checklist is what there is to show, as before copies existed.
  frozen() {
    const version = this.version();
    if (version == null) {
      return null;
    }
    return version.snapshot();
  }

  itemResults() {
    var data = this.get(ChecklistResult.Fields.CHECKLIST_ITEM_RESULTS);
    return new ChecklistItemResultMap(this.domain, data);
  }

  addChecklistItemResult(itemResult) {
    let itemResults = this.itemResults();
    itemResults.addChecklistItemResult(itemResult);
    this.withChecklistItemResults(itemResults);
    return this;
  }

  withChecklistItemResults(itemResults) {
    this.put(ChecklistResult.Fields.CHECKLIST_ITEM_RESULTS, itemResults.data);
    return this;
  }

  load() {
    this.withChecklist(this.checklist().find());
    this.withCompany(this.company().find());
    this.withVehicle(this.vehicle().find());
    this.withEmployee(this.employee().find());
    let results = this.itemResults().find();
    results.load();
    this.withItemResults(results);
  }
}